import dayjs from "dayjs";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Badge, Tooltip } from "../../utils/local-antd";
import { Flex } from "../../components/Flex";
import { faArrowDown } from "@fortawesome/free-solid-svg-icons/faArrowDown";

import { Rounded } from "../../theme/Rounded";
import { Spacing } from "../../theme/Spacing";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { integrationActions } from "../../redux/integration/integration.slice";
import { userSliceSelectors } from "../../redux/user/user.selector";
import { integrationSliceSelectors } from "../../redux/integration/integration.selector";
import { Text } from "../../components/Text";
import { AtiraEmpty } from "../../components/AtiraEmpty";
import { AtiraIcon } from "../../components/AtiraIcon";
import { WhatsAppMessage } from "./components/whatsapp-chat/WAMessage";
import { WAContactRow } from "./components/whatsapp-chat/WAContactRow";
import { WAChatbox } from "./components/whatsapp-chat/WAChatbox";
import {
  WHATSAPP_META_RECONNECT_PARAM,
  isChatHandledByAgent,
  whatsappChatScrollToBottom,
} from "./utils/integration-utils";
import { faHandPaper } from "@fortawesome/free-solid-svg-icons/faHandPaper";
import { Button } from "../../components/Button";
import { Spinner } from "../../components/Spinner";
import { SpinnerFullScreen } from "../../components/SpinnerFullScreen";
import { WANotesDrawer } from "./components/whatsapp-chat/WANotesDrawert";
import { useDrawersContext } from "../tasks/drawers-context";
import { Entry } from "../../model/entry/Entry";
import { EntityIcons } from "../../utils/GlobalIcons";
import { TestIDs } from "../../tests/types/testIds";
import { WAChatsSearch } from "./components/whatsapp-chat/WAChatsSearch";
import { WAEmptyThread } from "./components/whatsapp-chat/WAEmptyThread";
import { useWAChatsView } from "./components/whatsapp-chat/useWAChatsView";
import { WACreateChatModal } from "./components/whatsapp-chat/WACreateChatModal";
import { WAAgentBanner } from "./components/whatsapp-chat/WAAgentBanner";
import { WAReconnectAlert } from "./components/whatsapp-chat/WAReconnectAlert";
import { WACampaignsDrawer } from "./components/whatsapp-campaign/WACampaignsDrawer";
import { WADateSeparator } from "./components/whatsapp-chat/WADateSeparator";
import { WAAssignModal } from "./components/whatsapp-chat/WAAssignModal";
import { WAConversationHandler } from "../../model/integration/types/whatsapp/WAConversationHandler.enum";
import { permissionActions } from "../../redux/permission/permission.slice";
import { permissionSliceSelectors } from "../../redux/permission/permission.selector";
import { AtiraToast } from "../../utils/AtiraToast";
import { faRobot } from "@fortawesome/free-solid-svg-icons/faRobot";
import { faUserPlus } from "@fortawesome/free-solid-svg-icons/faUserPlus";
import { socket } from "../../Socketio";
import { ConversationMessage } from "../../model/integration/dto/meta/MetaConversationMessage";
import { WASystemNote } from "./components/whatsapp-chat/components/WASystemNote";
import { faCommentDots } from "@fortawesome/free-solid-svg-icons/faCommentDots";
import { WACommentNote } from "./components/whatsapp-chat/comments/WACommentNote";
import { WACommentsDrawer } from "./components/whatsapp-chat/comments/WACommentsDrawer";
import { WACommentModal } from "./components/whatsapp-chat/comments/WACommentModal";
import { WAThreadItem } from "../../model/integration/types/whatsapp/WAThreadItem";
import { WAUserAvatar } from "./components/whatsapp-chat/components/WAUserAvatar";
import { whatsappChatTitle } from "./utils/whatsapp-chat-title";
import { faTags } from "@fortawesome/free-solid-svg-icons/faTags";
import { faXmark } from "@fortawesome/free-solid-svg-icons/faXmark";
import { WATagsDropdown } from "./components/whatsapp-chat/WATagsDropdown";
import { WAChatsHeaderMenu } from "./components/whatsapp-chat/WAChatsHeaderMenu";
import { TagList } from "../../components/TagList";
import { tagSliceSelectors } from "../../redux/tag/tag.selector";

const Container = styled.div<{ $withActions: boolean }>`
  overflow: hidden;
  flex: 1;
  display: grid;
  grid-template-columns: ${({ $withActions }) =>
    $withActions
      ? "minmax(260px, 300px) 1fr minmax(260px, 320px)"
      : "minmax(260px, 300px) 1fr"};
  background: ${({ theme }) => theme.white};
  border: 1px solid ${({ theme }) => theme.darkSub};
  box-shadow: 0 10px 34px rgba(0, 0, 0, 0.08);
  min-height: 0;
  height: calc(100vh - 75px);
  height: calc(100dvh - 75px);
  max-height: calc(100vh - 150px);
  max-height: calc(100dvh - 150px);
  overflow: scroll;
  border-radius: ${Rounded.xl};
  margin: ${Spacing.l};
  margin-bottom: ${Spacing.m};
  overflow: hidden;

  @media (max-width: 1240px) {
    grid-template-columns: 260px 1fr;
  }

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
    height: auto;
    min-height: 620px;
  }
`;

const ReconnectAlertSlot = styled(Flex)`
  padding: ${Spacing.s} ${Spacing.m} 0;

  &:empty {
    display: none;
  }
`;

/** One fixed height for every column's header, so their bottom borders line
 * up across the page whatever each one holds
 */
const PanelHeader = styled(Flex)`
  position: sticky;
  top: 0;
  z-index: 2;
  box-sizing: border-box;
  height: 4.25rem;
  flex-shrink: 0;
  backdrop-filter: blur(6px);
  background: ${({ theme }) => theme.white};
  border-bottom: 1px solid ${({ theme }) => theme.darkSub};
  border-start-start-radius: 1rem;
`;

const SectionTitle = styled(Text)`
  font-weight: 700;
`;

const StyledBadge = styled(Badge)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${Rounded.circle};
  padding: ${Spacing.s};
  height: 1.5rem;
  width: 1.5rem;
  background: ${({ theme }) => theme.lightMain};
  color: ${({ theme }) => theme.white};
`;

const AttentionChip = styled.button<{ $on: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs} ${Spacing.s};
  border-radius: ${Rounded.pill};
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  border: 1px solid ${({ theme }) => theme.warning};
  background: ${({ $on, theme }) => ($on ? theme.warning : theme.transparent)};
  color: ${({ $on, theme }) => ($on ? theme.white : theme.warning)};
`;

const ChatsPanel = styled(Flex)`
  flex-direction: column;
  min-height: 0;
  height: 100%;
  min-width: 0;
  /* Logical sides, so the divider stays between the panels in Arabic too */
  border-inline-end: 1px solid ${({ theme }) => theme.darkSub};
  background: ${({ theme }) => theme.lightSub};
  border-end-start-radius: 1rem;
  border-start-start-radius: 1rem;

  @media (max-width: 980px) {
    max-height: 270px;
    border-inline-end: none;
    border-bottom: 1px solid ${({ theme }) => theme.darkSub};
  }
`;

const ChatsList = styled(Flex)`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;

  ::-webkit-scrollbar {
    width: 5px;
    height: 5px;
  }

  ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.darkSub};
    border-radius: ${Rounded.pill};
  }
`;

const ConversationShell = styled(Flex)<{ $withActions: boolean }>`
  position: relative;
  flex-direction: column;
  overflow: hidden;
  flex: 1;
  min-width: 0;
  height: 100%;
  min-height: 0;
  background: ${({ theme }) => theme.white};
  border-inline-end: ${({ theme, $withActions }) =>
    $withActions ? `1px solid ${theme.darkSub}` : "none"};

  @media (max-width: 1240px) {
    border-inline-end: none;
  }
`;

const JumpToLatest = styled.button`
  position: absolute;
  inset-inline-end: ${Spacing.l};
  bottom: 5rem;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.sub};
  border-radius: ${Rounded.circle};
  background-color: ${({ theme }) => theme.white};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);

  &:hover {
    background-color: ${({ theme }) => theme.lightSub};
  }
`;

const ConversationPanel = styled(Flex)`
  min-width: 0;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  background: ${({ theme }) => theme.lightSub};
  align-items: stretch;
  gap: ${Spacing.xs};
  direction: ltr;

  position: relative;
  scrollbar-width: thin;
  padding: ${Spacing.m};
  ::-webkit-scrollbar {
    width: 5px;
    height: 5px;
  }

  ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.darkSub};
    border-radius: ${Rounded.pill};
  }

  &:first-child {
    margin-top: auto !important;
  }
`;

const ChatEmptyPanel = styled(Flex)`
  flex: 1;
  min-height: 0;
`;

const ActionsPanel = styled(Flex)`
  min-width: 0;
  overflow-y: auto;
  background: ${({ theme }) => theme.lightSub};
  border-inline-start: 1px solid ${({ theme }) => theme.darkSub};

  @media (max-width: 1240px) {
    display: none;
  }
`;

const ActionButton = styled(Button)`
  margin: 0;
  width: 100%;
  min-width: 0;
  justify-content: flex-start;
  gap: ${Spacing.s};
  background-color: ${({ theme }) => theme.white};
  color: ${({ theme }) => theme.textColor};
  border: 1px solid ${({ theme }) => theme.darkSub};
  text-align: start;

  & > span,
  & > p {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }

  &:hover {
    background-color: ${({ theme }) => theme.sub};
    color: ${({ theme }) => theme.textColor};
  }
`;

const CommentsButton = styled(Button)`
  margin: 0;
  flex-shrink: 0;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs} ${Spacing.s};
  background-color: ${({ theme }) => theme.white};
  color: ${({ theme }) => theme.textColor};
  border: 1px solid ${({ theme }) => theme.darkSub};

  &:hover {
    background-color: ${({ theme }) => theme.sub};
    color: ${({ theme }) => theme.textColor};
  }
`;

const TagsFilterBar = styled(Flex)`
  flex-shrink: 0;
  align-items: center;
  gap: ${Spacing.s};
  padding: ${Spacing.s} ${Spacing.m};
  border-bottom: 1px solid ${({ theme }) => theme.darkSub};
  background: ${({ theme }) => theme.white};
  min-width: 0;
`;

const ClearFilterButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-inline-start: auto;
  padding: ${Spacing.xs};
  border: none;
  border-radius: ${Rounded.circle};
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => theme.lightMain};
  }
`;

export const WhatsAppMetaRoute: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeChatId = searchParams.get("chat") || "";

  const setActiveChatId = useCallback(
    (chatId: string) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);

          if (chatId) {
            next.set("chat", chatId);
          } else {
            next.delete("chat");
          }

          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const [isNotesDrawerOpen, setIsNotesDrawerOpen] = useState(false);
  const [createChatModalOpen, setCreateChatModalOpen] = useState(false);
  const [campaignsOpen, setCampaignsOpen] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [handBackLoading, setHandBackLoading] = useState(false);
  const [commentsDrawerOpen, setCommentsDrawerOpen] = useState(false);
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const activeChatIdRef = useRef(activeChatId);
  // Lands the thread at its newest message the first time it is drawn. Keyed
  // off the rendered messages rather than the fetch, because until they exist
  // the panel has nothing to scroll.
  const settledChatRef = useRef("");
  // Whether the reader has scrolled away from the newest message
  const [scrolledUp, setScrolledUp] = useState(false);

  // The message being replied to. Owned here because the menu that sets it and
  // the composer that shows it sit in different branches of the tree.
  const [replyTo, setReplyTo] = useState<ConversationMessage | null>(null);

  const onReply = useCallback((message: ConversationMessage) => {
    setReplyTo(message);

    // Picking a message to reply to is the first half of writing the reply
    requestAnimationFrame(() =>
      document
        .querySelector<HTMLInputElement>(".atira-wa-composer-input")
        ?.focus(),
    );
  }, []);

  /** Scrolls to a quoted message, fetching history until it is found.
   *
   * The quote resolves against loaded messages only, so replying to something
   * older than the current page left the jump doing nothing at all - which
   * reads as "sometimes the highlight works".
   */
  const jumpToMessage = useCallback(
    async (metaMessageId: string) => {
      const flash = () => {
        const node = document.querySelector(
          `[data-meta-message-id="${metaMessageId}"]`,
        );

        if (!node) {
          return false;
        }

        node.scrollIntoView({ behavior: "smooth", block: "center" });
        node.classList.add("atira-wa-message--flash");
        setTimeout(
          () => node.classList.remove("atira-wa-message--flash"),
          1600,
        );

        return true;
      };

      if (flash()) {
        return;
      }

      // Bounded: a quote from months back is not worth walking the whole
      // history for, and the quote itself already says it is unavailable
      for (let page = 0; page < 5; page++) {
        const older = await loadOlderMessages();

        if (!older) {
          break;
        }

        await new Promise((resolve) => requestAnimationFrame(resolve));

        if (flash()) {
          return;
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Which chat this mount has already asked for, so the refresh happens on
  // opening a chat rather than on every render
  const requestedChatRef = useRef("");
  const chatsListRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { setEntry, setEntryDetailsVisible } = useDrawersContext();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const whatsAppMetaIntegration = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaIntegration,
  );
  const chatsList = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaChatsList,
  );
  const totalUnread = useAppSelector(
    integrationSliceSelectors.selectTotalUnreadWhatsAppMetaChats,
  );
  const members = useAppSelector(permissionSliceSelectors.selectMembers);
  // A member works the chats assigned to them. Starting chats and deciding who
  // gets which stays with the owner - the API refuses both anyway.
  const isOrgMember = Boolean(
    useAppSelector(userSliceSelectors.selectIsUserOrgMember),
  );
  const comments = useAppSelector((state) =>
    integrationSliceSelectors.selectWhatsAppMetaComments(state, activeChatId),
  );
  const openCommentsCount = useAppSelector((state) =>
    integrationSliceSelectors.selectWhatsAppMetaOpenCommentsCount(
      state,
      activeChatId,
    ),
  );

  const activeChat = chatsList?.find((chat) => chat._id === activeChatId);
  const {
    visibleChats,
    loading: chatsLoading,
    loadingMore: chatsLoadingMore,
    filtered: chatsFiltered,
    onListScroll,
    resolvingChat,
    attentionCount,
    onlyNeedsAttention,
    toggleNeedsAttention,
    filterTagIds,
    toggleFilterTag,
    clearFilterTags,
    searchOpen,
    searchText,
    setSearchText,
    toggleSearch,
    closeSearch,
  } = useWAChatsView({
    activeChatId,
    setActiveChatId,
    listRef: chatsListRef,
  });
  const tags = useAppSelector(tagSliceSelectors.selectTags);

  const onToggleChatTag = useCallback(
    async (tagId: string) => {
      if (!activeChat || !whatsAppMetaIntegration) {
        return;
      }

      const hasTag = activeChat.tagIds?.includes(tagId);

      try {
        await dispatch(
          integrationActions.updateWhatsappMetaConversationTags({
            userId,
            integrationId: whatsAppMetaIntegration._id,
            conversationId: activeChat._id,
            ...(hasTag ? { remove: [tagId] } : { add: [tagId] }),
          }),
        ).unwrap();
        await dispatch(
          integrationActions.getWhatsAppMetaChat({
            userId,
            integrationId: whatsAppMetaIntegration._id,
            conversationId: activeChat._id,
          }),
        ).unwrap();
      } catch (e) {
        console.log(e);
        AtiraToast.apiError(e);
      }
    },
    [dispatch, userId, activeChat, whatsAppMetaIntegration],
  );

  const agent = whatsAppMetaIntegration?.config?.aiAgent;
  const handledByAgent = isChatHandledByAgent(
    activeChat,
    whatsAppMetaIntegration,
  );
  // The chat carries its assignees populated. The members list is the fallback
  // for a chat that arrived without them, and is empty for a member, who cannot
  // list the organization.
  const assignees = activeChat?.assignees?.length
    ? activeChat.assignees
    : members.filter((m) => activeChat?.assigneeIds?.includes(m._id));

  const paging = useAppSelector((state) =>
    integrationSliceSelectors.selectWhatsAppMetaMessagesPaging(
      state,
      activeChatId,
    ),
  );

  /** Fetches a page of a conversation's messages.
   *
   * With no cursor that is the newest page, which is what opening a chat
   * needs; with one it is the page before it, which is what scrolling up
   * needs. One function so the two cannot drift apart.
   */
  const loadMessagesPage = useCallback(
    async (
      chatId: string,
      options: {
        before?: string;
        beforeId?: string;
        silent?: boolean;
        replace?: boolean;
      } = {},
    ) => {
      const { before, beforeId, silent, replace } = options;

      if (!silent) {
        dispatch(
          integrationActions.metaSetMessagesLoading({
            conversationId: chatId,
            loading: true,
          }),
        );
      }

      try {
        const page = await dispatch(
          integrationActions.getWhatsappMetaMessages({
            userId,
            integrationId: whatsAppMetaIntegration!._id,
            conversationId: chatId,
            before,
            beforeId,
          }),
        ).unwrap();

        dispatch(
          replace
            ? integrationActions.metaSetMessages({
                conversationId: chatId,
                messages: page.messages,
                hasMore: page.hasMore,
              })
            : integrationActions.metaPrependMessages({
                conversationId: chatId,
                messages: page.messages,
                hasMore: page.hasMore,
              }),
        );

        return page;
      } catch (e) {
        console.log(e);

        dispatch(
          integrationActions.metaSetMessagesLoading({
            conversationId: chatId,
            loading: false,
          }),
        );

        return undefined;
      }
    },
    [dispatch, userId, whatsAppMetaIntegration],
  );

  const loadOlderMessages = async () => {
    const panel = document.body.querySelector(".atira-meta-conversation-panel");
    // A bubble we are still uploading has a local id, and paging from it would
    // hand the API something it cannot turn into an ObjectId
    const oldest = orderedMessages.find((message) => !message.pending);

    if (
      !activeChat ||
      !oldest ||
      paging?.loading ||
      paging?.hasMore === false
    ) {
      return;
    }

    const previousHeight = panel?.scrollHeight || 0;

    const page = await loadMessagesPage(activeChat._id, {
      before: oldest.timestamp,
      beforeId: oldest._id,
    });

    if (!page) {
      return false;
    }

    // Keeps the reading position where it was rather than jumping to the top
    requestAnimationFrame(() => {
      if (panel) {
        panel.scrollTop = panel.scrollHeight - previousHeight;
      }
    });

    return page.hasMore;
  };

  const orderedMessages = useMemo(
    () =>
      [...(activeChat?.messages || [])].sort(
        (a, b) => Number(a.timestamp) - Number(b.timestamp),
      ),
    [activeChat?.messages],
  );

  /** The thread as it is drawn: the messages, with the chat's comments placed
   * among them by when they were written.
   *
   * Only comments within the stretch of history already on screen. One older
   * than the oldest loaded message belongs to a page nobody has scrolled back
   * to yet, and would otherwise sit at the top of the thread out of order.
   */
  const timeline = useMemo<WAThreadItem[]>(() => {
    const items: WAThreadItem[] = orderedMessages.map((message) => ({
      kind: "message",
      at: Number(message.timestamp),
      message,
    }));

    const oldest = items[0]?.at;
    const wholeHistoryLoaded = paging?.hasMore === false;

    for (const comment of comments || []) {
      const at = Math.floor(new Date(comment.createdAt).getTime() / 1000);

      if (wholeHistoryLoaded || (oldest !== undefined && at >= oldest)) {
        items.push({ kind: "comment", at, comment });
      }
    }

    // Stable, so a comment written in the same second as a message follows it
    return items.sort((a, b) => a.at - b.at);
  }, [orderedMessages, comments, paging?.hasMore]);

  // A comment lands at the bottom of the thread, so follow it there - unless
  // the reader has scrolled up to read something older
  const commentsSeenRef = useRef({ chatId: "", count: 0 });

  useEffect(() => {
    const count = comments?.length || 0;
    const seen = commentsSeenRef.current;

    commentsSeenRef.current = { chatId: activeChatId, count };

    if (seen.chatId === activeChatId && count > seen.count && !scrolledUp) {
      requestAnimationFrame(whatsappChatScrollToBottom);
    }
  }, [comments?.length, activeChatId, scrolledUp]);

  const messagesByMetaId = useMemo(
    () =>
      new Map(
        orderedMessages
          .filter((message) => message.metaMessageId)
          .map((message) => [message.metaMessageId, message]),
      ),
    [orderedMessages],
  );

  const onHandBackToAgent = async () => {
    if (!activeChat) {
      return;
    }

    try {
      setHandBackLoading(true);

      await dispatch(
        integrationActions.setWhatsappMetaConversationHandler({
          userId,
          integrationId: whatsAppMetaIntegration!._id,
          conversationId: activeChat._id,
          handledBy: WAConversationHandler.AI,
        }),
      ).unwrap();

      AtiraToast.success(
        t("integration.whatsapp.meta.aiAgent.handBack.success"),
      );
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setHandBackLoading(false);
    }
  };

  const openEntry = () => {
    if (!activeChat?.entry) {
      return;
    }

    setEntry(activeChat.entry as Entry);
    setEntryDetailsVisible(true);
  };

  useEffect(() => {
    setReplyTo(null);
    setScrolledUp(false);
    setCommentsDrawerOpen(false);
  }, [activeChatId]);

  // Fetched with the chat rather than when the drawer opens, so the header can
  // say how many are open before anyone looks
  useEffect(() => {
    if (!activeChatId || !whatsAppMetaIntegration?._id) {
      return;
    }

    dispatch(
      integrationActions.getWhatsappMetaComments({
        userId,
        integrationId: whatsAppMetaIntegration._id,
        conversationId: activeChatId,
      }),
    );
  }, [dispatch, activeChatId, userId, whatsAppMetaIntegration?._id]);

  useEffect(() => {
    // Waits for the chat itself: the list is not kept across a reload, so a
    // chat named in the link arrives after this would have fired, and a page
    // of messages with no chat to land in was dropped
    if (
      !activeChatId ||
      !activeChat ||
      !whatsAppMetaIntegration ||
      requestedChatRef.current === activeChatId
    ) {
      return;
    }

    requestedChatRef.current = activeChatId;

    const chatId = activeChatId;

    loadMessagesPage(chatId, {
      replace: true,
      silent: Boolean(activeChat?.messages?.length),
    }).then(() =>
      requestAnimationFrame(() => {
        if (activeChatIdRef.current !== chatId) {
          return;
        }

        whatsappChatScrollToBottom();
        // Settled only once the fresh page is on screen and the panel is at
        // its bottom. Settled on the few messages the chat list already held,
        // the page that replaced them was never scrolled, and a preview too
        // short to scroll left the thread at its very top. Settled before the
        // scroll, the load-older gate opened at scrollTop 0 and fetched a page
        // of history nobody asked for.
        settledChatRef.current = chatId;
      }),
    );
  }, [activeChatId, whatsAppMetaIntegration, activeChat, loadMessagesPage]);

  // Keeps whatever is already on screen at the bottom until the fresh page
  // settles it
  useEffect(() => {
    if (
      !activeChatId ||
      !orderedMessages.length ||
      settledChatRef.current === activeChatId
    ) {
      return;
    }

    requestAnimationFrame(whatsappChatScrollToBottom);
  }, [activeChatId, orderedMessages.length]);

  useEffect(() => {
    if (
      !whatsAppMetaIntegration?._id ||
      !whatsAppMetaIntegration?.config?.phoneNumberId ||
      !userId
    ) {
      return;
    }

    dispatch(
      integrationActions.getWhatsAppMetaChatsList({
        userId,
        integrationId: whatsAppMetaIntegration._id,
        phoneNumberId: whatsAppMetaIntegration.config.phoneNumberId,
      }),
    );

    dispatch(
      integrationActions.getWhatsaAppMetaTemplates({
        userId,
        integrationId: whatsAppMetaIntegration._id,
      }),
    );

    dispatch(
      integrationActions.getWhatsappMetaMediaStorage({
        userId,
        integrationId: whatsAppMetaIntegration._id,
      }),
    );

    // Only the owner has an organization to list, and only the owner assigns
    if (!isOrgMember) {
      dispatch(permissionActions.getMembers({ userId }));
      dispatch(
        integrationActions.getWhatsappMetaCampaigns({
          userId,
          integrationId: whatsAppMetaIntegration._id,
        }),
      );
    }
  }, [dispatch, userId, whatsAppMetaIntegration?._id, isOrgMember]);

  useEffect(() => {
    if (!whatsAppMetaIntegration?._id) {
      return;
    }

    const onMessageIn = (data: any) => {
      if (activeChatIdRef.current === data.conversationId) {
        dispatch(
          integrationActions.markWhatsAppMetaChatAsRead({
            userId,
            integrationId: whatsAppMetaIntegration._id!,
            conversationId: activeChatIdRef.current,
          }),
        );

        requestAnimationFrame(whatsappChatScrollToBottom);
      }
    };

    const onMessageOut = (data: any) => {
      if (activeChatIdRef.current === data.conversationId) {
        requestAnimationFrame(whatsappChatScrollToBottom);
      }
    };

    // The thread open on screen missed whatever came while the socket was down.
    // Fetched the way opening the chat fetches it, so a reconnect runs nothing
    // that opening a chat does not.
    const onReconnect = () => {
      const conversationId = activeChatIdRef.current;

      if (!conversationId) {
        return;
      }

      loadMessagesPage(conversationId, { replace: true, silent: true });

      dispatch(
        integrationActions.getWhatsappMetaComments({
          userId,
          integrationId: whatsAppMetaIntegration._id!,
          conversationId,
        }),
      );
    };

    socket.on("message.in", onMessageIn);
    socket.on("message.out", onMessageOut);
    socket.io.on("reconnect", onReconnect);

    return () => {
      socket.off("message.in", onMessageIn);
      socket.off("message.out", onMessageOut);
      socket.io.off("reconnect", onReconnect);
    };
  }, [whatsAppMetaIntegration?._id, dispatch, userId, loadMessagesPage]);

  useEffect(() => {
    if (!activeChatId) {
      return;
    }

    dispatch(
      integrationActions.markWhatsAppMetaChatAsRead({
        userId,
        integrationId: whatsAppMetaIntegration?._id!,
        conversationId: activeChatId,
      }),
    );
  }, [dispatch, activeChatId, userId, whatsAppMetaIntegration?._id]);

  useEffect(() => {
    if (totalUnread > 0) {
      document.title = `(${totalUnread || 0}) ${t("app.name")}`;
    } else {
      document.title = t("app.name");
    }

    return () => {
      document.title = t("app.name");
    };
  }, [totalUnread]);

  useEffect(() => {
    activeChatIdRef.current = activeChatId;

    if (activeChatId) {
      requestAnimationFrame(() => {
        whatsappChatScrollToBottom();
      });
    }
  }, [activeChatId]);

  if (!whatsAppMetaIntegration) {
    // The owner reaches this page from their connected integration. A member
    // is sent here directly, so say why it is empty rather than render nothing.
    return isOrgMember ? (
      <ChatEmptyPanel
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        <AtiraEmpty
          description={t("integration.whatsapp.meta.member.notConnected")}
        />
      </ChatEmptyPanel>
    ) : null;
  }

  return (
    <Container $withActions={Boolean(activeChat)}>
      <ChatsPanel flexDirection="column">
        <PanelHeader
          alignItems="center"
          justifyContent="space-between"
          padding="m"
          gap="s"
        >
          <Flex alignItems="center" gap="s">
            <SectionTitle fontSize="m">
              {t("integration.whatsapp.chatsPanel.title")}
            </SectionTitle>
            {totalUnread ? (
              <StyledBadge
                data-testid={TestIDs.integration.whatsappMeta.chat.unreadTotal}
              >
                {totalUnread}
              </StyledBadge>
            ) : null}

            {attentionCount ? (
              // Icon and count only: the header also holds the title and three
              // buttons, and the label wrapped onto two lines in Arabic
              <Tooltip
                title={t("integration.whatsapp.meta.handover.filter", {
                  count: attentionCount,
                })}
              >
                <AttentionChip
                  type="button"
                  data-testid={
                    TestIDs.integration.whatsappMeta.chat.attentionChip
                  }
                  aria-label={t("integration.whatsapp.meta.handover.filter", {
                    count: attentionCount,
                  })}
                  aria-pressed={onlyNeedsAttention}
                  $on={onlyNeedsAttention}
                  onClick={toggleNeedsAttention}
                >
                  <AtiraIcon
                    icon={faHandPaper}
                    size="xs"
                    color={onlyNeedsAttention ? "white" : "warning"}
                  />
                  <Text
                    fontSize="xs"
                    color={onlyNeedsAttention ? "white" : "warning"}
                  >
                    {attentionCount}
                  </Text>
                </AttentionChip>
              </Tooltip>
            ) : null}
          </Flex>
          <WAChatsHeaderMenu
            isOrgMember={isOrgMember}
            searchOpen={searchOpen}
            onToggleSearch={toggleSearch}
            filterTagIds={filterTagIds}
            onToggleFilterTag={toggleFilterTag}
            onOpenCampaigns={() => setCampaignsOpen(true)}
            onCreateChat={() => setCreateChatModalOpen(true)}
          />
        </PanelHeader>

        <ReconnectAlertSlot>
          <WAReconnectAlert
            onReconnect={() =>
              navigate(`/integration?${WHATSAPP_META_RECONNECT_PARAM}=1`)
            }
          />
        </ReconnectAlertSlot>

        {searchOpen ? (
          <WAChatsSearch
            value={searchText}
            onChange={setSearchText}
            onClose={closeSearch}
          />
        ) : null}

        {filterTagIds.length ? (
          <TagsFilterBar>
            <Text fontSize="s" color="darkerSub" style={{ flexShrink: 0 }}>
              {t("integration.whatsapp.chatsPanel.tagsFilter.active")}
            </Text>
            <Flex minWidth="0" overflow="hidden">
              <TagList tagIds={filterTagIds} max={2} />
            </Flex>
            <ClearFilterButton
              type="button"
              title={t("integration.whatsapp.chatsPanel.tagsFilter.clear")}
              aria-label={t("integration.whatsapp.chatsPanel.tagsFilter.clear")}
              data-testid={
                TestIDs.integration.whatsappMeta.chat.tagsFilter.clear
              }
              onClick={clearFilterTags}
            >
              <AtiraIcon icon={faXmark} size="sm" />
            </ClearFilterButton>
          </TagsFilterBar>
        ) : null}

        <ChatsList
          flexDirection="column"
          flex={1}
          dataTestId={TestIDs.integration.whatsappMeta.chat.list}
          ref={chatsListRef}
          onScroll={onListScroll}
        >
          {chatsLoading ? (
            <SpinnerFullScreen />
          ) : !visibleChats?.length && chatsFiltered ? (
            <Flex
              padding="l"
              justifyContent="center"
              dataTestId={TestIDs.integration.whatsappMeta.chat.search.empty}
            >
              <AtiraEmpty
                description={t(
                  searchText.trim()
                    ? "integration.whatsapp.chatsPanel.search.empty"
                    : "integration.whatsapp.chatsPanel.filter.empty",
                )}
              />
            </Flex>
          ) : !visibleChats?.length && isOrgMember ? (
            // A member only ever sees what is assigned to them, so an empty
            // list is worth explaining rather than leaving blank
            <Flex padding="l" justifyContent="center">
              <AtiraEmpty
                description={t("integration.whatsapp.meta.member.noChats")}
              />
            </Flex>
          ) : !visibleChats?.length ? (
            <Flex
              padding="l"
              flexDirection="column"
              alignItems="center"
              gap="s"
            >
              <AtiraEmpty
                description={t("integration.whatsapp.chatsPanel.empty")}
              />
              <Text color="darkerSub" fontSize="s" align="center">
                {t("integration.whatsapp.chatsPanel.emptyHint")}
              </Text>
            </Flex>
          ) : (
            <>
              {visibleChats.map((chat) => (
                <WAContactRow
                  key={chat._id}
                  chat={chat}
                  activeChatId={activeChatId}
                  setActiveChatId={setActiveChatId}
                />
              ))}

              {chatsLoadingMore ? (
                <Flex justifyContent="center" padding="s" width="100%">
                  <Spinner color="main" size="1.5rem" />
                </Flex>
              ) : null}
            </>
          )}
        </ChatsList>
      </ChatsPanel>

      <ConversationShell
        width="100%"
        flexDirection="column"
        overflow="hidden"
        $withActions={Boolean(activeChat)}
      >
        <PanelHeader
          alignItems="center"
          justifyContent="space-between"
          padding="m"
          gap="s"
        >
          <SectionTitle fontSize="m">
            {whatsappChatTitle(activeChat) || "Chat"}
            {activeChat?.name && activeChat.waId ? ` (${activeChat.waId})` : ""}
            {activeChat?.name && !activeChat.waId && activeChat.username
              ? ` (@${activeChat.username})`
              : ""}
          </SectionTitle>

          {activeChat ? (
            <Badge
              count={openCommentsCount}
              size="small"
              overflowCount={99}
              offset={[-4, 4]}
            >
              <CommentsButton
                icon={faCommentDots}
                onClick={() => setCommentsDrawerOpen(true)}
              >
                {t("integration.whatsapp.meta.comments.view")}
              </CommentsButton>
            </Badge>
          ) : null}
        </PanelHeader>

        <ConversationPanel
          className="atira-meta-conversation-panel"
          onScroll={(event) => {
            const panel = event.currentTarget;

            // A freshly rendered thread sits at scrollTop 0 until the settle
            // below sends it to the bottom, and that counts as "scrolled to
            // the top" - so opening a chat fetched a page of history nobody
            // asked for, and showed a second spinner doing it.
            if (
              panel.scrollTop <= 40 &&
              settledChatRef.current === activeChatId
            ) {
              loadOlderMessages();
            }

            // Far enough up that new messages would arrive off-screen
            setScrolledUp(
              panel.scrollHeight - panel.scrollTop - panel.clientHeight > 240,
            );
          }}
        >
          {activeChat && paging?.loading && !orderedMessages.length ? (
            <Flex justifyContent="center" alignItems="center" height="100%">
              <Spinner color="main" size="2rem" />
            </Flex>
          ) : null}

          {activeChat ? (
            <>
              {paging?.loaded && !paging.loading && !timeline.length ? (
                <WAEmptyThread />
              ) : null}
              {paging?.loading && orderedMessages.length ? (
                <Flex justifyContent="center" padding="s" width="100%">
                  {/* Without a colour this is theme.sub, which is the panel */}
                  <Spinner color="main" size="1.5rem" />
                </Flex>
              ) : null}
              {timeline.map((item, index) => {
                if (item.kind === "comment") {
                  return (
                    <WACommentNote
                      key={item.comment._id}
                      comment={item.comment}
                      onOpen={() => setCommentsDrawerOpen(true)}
                    />
                  );
                }

                const { message } = item;
                const previous = timeline[index - 1];
                const startsNewDay =
                  !previous ||
                  !dayjs(previous.at * 1000).isSame(
                    dayjs(item.at * 1000),
                    "day",
                  );

                if (message.type === "system") {
                  return <WASystemNote key={message._id} message={message} />;
                }

                return (
                  <Fragment key={message._id}>
                    {startsNewDay ? (
                      <WADateSeparator timestamp={message.timestamp} />
                    ) : null}
                    <WhatsAppMessage
                      message={message}
                      customerName={whatsappChatTitle(activeChat)}
                      agentName={
                        agent?.name ||
                        t("integration.whatsapp.meta.aiAgent.defaultName")
                      }
                      onReply={onReply}
                      onJumpToMessage={jumpToMessage}
                      repliedTo={
                        message.context?.id
                          ? messagesByMetaId.get(message.context.id)
                          : undefined
                      }
                    />
                  </Fragment>
                );
              })}
            </>
          ) : activeChatId && resolvingChat ? (
            <Flex justifyContent="center" alignItems="center" height="100%">
              <Spinner color="main" size="2rem" />
            </Flex>
          ) : (
            <ChatEmptyPanel
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap="m"
              width="100%"
            >
              <AtiraEmpty
                description={t("integration.whatsapp.meta.chat.no-active")}
              />
            </ChatEmptyPanel>
          )}
        </ConversationPanel>

        {activeChat && scrolledUp && orderedMessages.length ? (
          <JumpToLatest
            type="button"
            onClick={() => {
              whatsappChatScrollToBottom();
              setScrolledUp(false);
            }}
            title={t("integration.whatsapp.meta.jumpToLatest")}
          >
            <AtiraIcon icon={faArrowDown} size="sm" color="main" />
          </JumpToLatest>
        ) : null}

        {handledByAgent && activeChat ? (
          <WAAgentBanner chat={activeChat} />
        ) : (
          <WAChatbox
            activeChatId={activeChatId}
            replyTo={replyTo}
            onCancelReply={() => setReplyTo(null)}
          />
        )}
      </ConversationShell>

      {activeChat ? (
        <ActionsPanel flexDirection="column">
          <PanelHeader
            alignItems="center"
            justifyContent="space-between"
            padding="m"
            gap="s"
          >
            <SectionTitle fontSize="m">{t("common.actions")}</SectionTitle>
          </PanelHeader>

          <Flex flexDirection="column" padding="m" gap="s">
            {activeChat.entry ? (
              <ActionButton onClick={openEntry} icon={EntityIcons.Entries}>
                {t("integration.whatsapp.direct.chat.openContact")}
              </ActionButton>
            ) : null}

            <ActionButton
              onClick={() => setCommentModalOpen(true)}
              icon={faCommentDots}
            >
              {t("integration.whatsapp.meta.comments.add")}
            </ActionButton>

            {!isOrgMember ? (
              <ActionButton
                onClick={() => setAssignModalOpen(true)}
                icon={faUserPlus}
              >
                {t("integration.whatsapp.meta.assign.title")}
              </ActionButton>
            ) : null}

            <WATagsDropdown
              selectedIds={activeChat.tagIds || []}
              onToggle={onToggleChatTag}
              testIds={TestIDs.integration.whatsappMeta.chat.tags}
            >
              <ActionButton icon={faTags}>{t("common.tags")}</ActionButton>
            </WATagsDropdown>

            {agent?.enabled && !handledByAgent ? (
              <ActionButton
                onClick={onHandBackToAgent}
                icon={faRobot}
                loading={handBackLoading}
                disabled={handBackLoading}
              >
                {t("integration.whatsapp.meta.aiAgent.handBack.button", {
                  name:
                    agent.name ||
                    t("integration.whatsapp.meta.aiAgent.defaultName"),
                })}
              </ActionButton>
            ) : null}

            <Flex flexDirection="column" gap="xs" marginTop="m">
              <Text fontWeight="bold" fontSize="s">
                {t("common.assignees")}
              </Text>

              {handledByAgent ? (
                <Flex alignItems="center" gap="xs">
                  <AtiraIcon icon={faRobot} color="main" size="xs" />
                  <Text fontSize="s" color="main">
                    {agent?.name ||
                      t("integration.whatsapp.meta.aiAgent.defaultName")}
                  </Text>
                </Flex>
              ) : null}

              {assignees.length ? (
                assignees.map((assignee) => (
                  <Flex key={assignee._id} alignItems="center" gap="s">
                    <WAUserAvatar user={assignee} />
                    <Text fontSize="s" color="darkerSub">
                      {assignee.name}
                    </Text>
                  </Flex>
                ))
              ) : handledByAgent ? null : (
                <Text fontSize="s" color="darkerSub">
                  {t("integration.whatsapp.meta.assign.unassigned")}
                </Text>
              )}
            </Flex>

            <Flex flexDirection="column" gap="xs" marginTop="m">
              <Text fontWeight="bold" fontSize="s">
                {t("common.tags")}
              </Text>

              {tags.some((tag) => activeChat.tagIds?.includes(tag._id)) ? (
                <TagList tagIds={activeChat.tagIds} wrap />
              ) : (
                <Text fontSize="s" color="darkerSub">
                  {t("integration.whatsapp.meta.tags.none")}
                </Text>
              )}
            </Flex>
          </Flex>
        </ActionsPanel>
      ) : null}

      <WANotesDrawer
        open={isNotesDrawerOpen}
        onClose={() => setIsNotesDrawerOpen(false)}
        activeChatId={activeChatId}
      />

      {!isOrgMember ? (
        <WACampaignsDrawer
          open={campaignsOpen}
          onClose={() => setCampaignsOpen(false)}
        />
      ) : null}

      <WACreateChatModal
        isOpen={createChatModalOpen}
        onClose={() => setCreateChatModalOpen(false)}
        setActiveChatId={setActiveChatId}
      />

      {activeChat && !isOrgMember ? (
        <WAAssignModal
          open={assignModalOpen}
          onClose={() => setAssignModalOpen(false)}
          chat={activeChat}
        />
      ) : null}

      {activeChat ? (
        <Fragment>
          <WACommentsDrawer
            open={commentsDrawerOpen}
            onClose={() => setCommentsDrawerOpen(false)}
            conversationId={activeChat._id}
            title={`${t("integration.whatsapp.meta.comments.title")} · ${whatsappChatTitle(
              activeChat,
            )}`}
          />

          <WACommentModal
            open={commentModalOpen}
            onClose={() => setCommentModalOpen(false)}
            conversationId={activeChat._id}
          />
        </Fragment>
      ) : null}
    </Container>
  );
};
